import React, { useState, useEffect } from 'react';
import { Box, Button, Typography, Grid, FormControl, Select, MenuItem, InputLabel, IconButton, FormHelperText } from '@mui/material';
import { useHistory, useLocation } from 'react-router-dom';
// import DeleteIcon from '@mui/icons-material/Delete';
import deleteIcon from 'assets/images/delete_icon.svg';
import { commonStyles as styles } from 'assets/style';
import ErrorFallback from 'components/error-fallback';
import { ErrorBoundary } from 'react-error-boundary';
import { getLoggedUserGPID, getLoggedUserFullName } from 'utils';
import { PEPVIGIL } from 'pages/dlops-dashboard/utils/constant';
import { LoggedUserEmailId } from '../utils/index';
import { Item, style } from '../style';
import StyleTextField from '../styleTextField';
import breadcrumbIcon from '../../../assets/images/dlopsImages/arrow_forward.svg';

const EditExistingModel = () => {
    const [modelName, setModelName] = useState('');
    const [modelDescription, setModelDescription] = useState('');
    const [modelVersion, setModelVersion] = useState('');
    // const [allListOfProjects, setAllListOfProjects] = useState('');
    const [modelType, setModelType] = useState('');
    const [modelNameError, setModelNameError] = useState('');
    const [modelVersionError, setModelVersionError] = useState('');
    const [modelTypeError, setModelTypeError] = useState('');
    const [emailError, setEmailError] = useState('');
    const [metricsError, setMetricsError] = useState([]);
    const [newMetricsError, setNewMetricsError] = useState([]);
    const history = useHistory();
    const location = useLocation();
    const activeModelData = location.state?.state?.activeData;
    const [newlyAddedEmails, setNewlyAddedEmails] = useState([]);
    const [inputEmails, setInputEmail] = useState('');
    const [metrics, setMetrics] = useState([{ key: 'accuracy', value: ['', ''] }]);
    const [newMetric, setNewMetric] = useState([]);
    const [segmentationSubType, setSegmentationSubType] = useState('');
    const [segmentationSubTypeError, setSegmentationSubTypeError] = useState('');

    useEffect(() => {
        if (activeModelData) {
            const initialData = JSON.parse(localStorage.getItem('initialModelData'));
            if (!initialData) {
                localStorage.setItem('initialModelData', JSON.stringify(activeModelData));
            }
            // Set state for model name, description, version, and type
            setModelName(activeModelData.name || '');
            setModelDescription(activeModelData.description || '');
            setModelVersion(activeModelData.version || '');
            const modelTypeFromData = activeModelData.tags.find((tag) => tag.key === 'model_type')?.value || '';
            setModelType(modelTypeFromData.charAt(0).toUpperCase() + modelTypeFromData.slice(1));

            const segmentationSubTypeFromData = activeModelData.tags.find((tag) => tag.key === 'segmentation_sub_type')?.value || '';
            setSegmentationSubType(segmentationSubTypeFromData);

            // Set distribution list emails
            const emails = activeModelData.tags.find((tag) => tag.key === 'distribution_list')?.value.split(',') || [];
            setNewlyAddedEmails(emails);

            // Set metrics
            const accuracyMetrics = activeModelData.tags.find((tag) => tag.key === 'accuracy');
            if (accuracyMetrics) {
                try {
                    const parsedAccuracyMetrics = JSON.parse(accuracyMetrics.value);
                    setMetrics([{ key: 'accuracy', value: parsedAccuracyMetrics }]);
                } catch (error) {
                    // Set a default value if parsing fails
                    setMetrics([{ key: 'accuracy', value: ['', ''] }]);
                }
            }
            const metricsData = activeModelData.tags.find((tag) => tag.key === 'model_metrics');
            const parsedMetrics = metricsData.value
                .replace(/[['\]]/g, '') // Remove brackets and single quotes
                .split(',')
                .map((metric) => metric.trim());
            const newMetricsData = activeModelData.tags.filter((tag) => {
                // Check if the tag is not the model_metrics and if its key is included in parsedMetrics
                return tag.key !== 'model_metrics' && parsedMetrics.includes(tag.key);
            });

            // Filter out the 'accuracy' metric
            const notAccuracyMetrics = newMetricsData.filter((metric) => metric.key !== 'accuracy');

            // Map to create an array of new metrics, handling potential JSON parsing errors
            if (notAccuracyMetrics) {
                const newMetrics = notAccuracyMetrics.map((tag) => {
                    // let parsedValue;
                    try {
                        return {
                            key: tag.key,
                            value: JSON.parse(tag.value),
                        };
                    } catch (error) {
                        // Handle or return default values if parsing fails
                        return {
                            key: tag.key,
                            value: [], // or some default value
                        };
                    }
                });

                setNewMetric(newMetrics);
            }
        }
    }, [activeModelData]);

    const handleAddEmail = () => {
        if (inputEmails) {
            const totalLength = newlyAddedEmails.reduce((acc, email) => acc + email.length, 0) + inputEmails.length;

            // Check if the total length exceeds 200 characters
            if (totalLength > 200) {
                setEmailError('Total length of emails cannot exceed 200 characters.');
                return;
            }
            // Check if email already exists in the list
            if (newlyAddedEmails.includes(inputEmails)) {
                setEmailError('This email is already added.');
            }
            // Check for valid email format and that it ends with @pepsico.com
            else if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(inputEmails) && inputEmails.endsWith('@pepsico.com')) {
                setNewlyAddedEmails([...newlyAddedEmails, inputEmails]);
                setInputEmail('');
                setEmailError('');
            }
            // Check for valid email but with wrong domain
            else if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(inputEmails)) {
                setEmailError('Email must be a @pepsico.com address.');
            } else {
                setEmailError('Please enter a valid email address.');
            }
        } else {
            setEmailError('Email address cannot be empty.');
        }
    };
    const handleDeleteEmail = (emailToDelete) => {
        setNewlyAddedEmails(newlyAddedEmails.filter((email) => email !== emailToDelete));
    };

    useEffect(() => {
        const storedData = JSON.parse(localStorage.getItem('editModelFormData'));
        if (storedData) {
            setModelName(storedData.model_name || '');
            setModelDescription(storedData.model_desc || '');
            setModelVersion(storedData.model_version || '');
            setModelType(storedData.tags.find((tag) => tag.mkey === 'model_type')?.mvalue || '');

            setSegmentationSubType(storedData.tags.find((tag) => tag.mkey === 'segmentation_sub_type')?.mvalue || '');

            setNewlyAddedEmails(storedData.tags.find((tag) => tag.mkey === 'distribution_list')?.mvalue.split(',') || []);
            // Retrieve and set existing metrics
            const metricsData = storedData.tags.find((tag) => tag.mkey === 'model_metrics');
            if (metricsData) {
                const parsedMetrics = metricsData.mvalue
                    .replace(/[['\]]/g, '') // Remove brackets and single quotes
                    .split(',')
                    .map((metric) => metric.trim());
                const accuracyMetrics = parsedMetrics.filter((metric) => metric.trim() === 'accuracy');
                // Set existing metrics
                const retrievedMetrics = accuracyMetrics.map((metric) => {
                    const metricTag = storedData.tags.find((t) => t.mkey === metric);
                    return {
                        key: metric,
                        value: metricTag ? JSON.parse(metricTag.mvalue) : ['', ''],
                    };
                });
                setMetrics(retrievedMetrics);
                // Filter and set newly added metrics
                const newMetricsData = storedData.tags.filter((tag) => tag.mkey !== 'model_metrics' && parsedMetrics.includes(tag.mkey));
                const notAccuracyMetrics = newMetricsData.filter((metric) => metric.mkey !== 'accuracy');
                const newMetrics = notAccuracyMetrics.map((tag) => {
                    try {
                        return {
                            key: tag.mkey,
                            value: JSON.parse(tag.mvalue),
                        };
                    } catch (error) {
                        // Handle or return default values if parsing fails
                        return {
                            key: tag.mkey,
                            value: [], // or some default value
                        };
                    }
                });
                setNewMetric(newMetrics);
            }
        }
        // const storedProjects = JSON.parse(localStorage.getItem('all_project_list')) || [];
        // setAllListOfProjects(storedProjects);
    }, []);
    const validateEmail = (email) => {
        const emailPattern = /\S+@\S+\.\S+/;
        return emailPattern.test(email);
    };
    const validateForm = () => {
        let isValid = true;
        // Model Name validation
        if (!modelName) {
            setModelNameError('Model Name is required');
            isValid = false;
        } else {
            setModelNameError('');
        }
        // Model Version validation
        const versionPattern = /^\d+(\.\d+)?$/; // Allows only numbers and decimals
        if (!modelVersion) {
            setModelVersionError('Model Version is required');
            isValid = false;
        } else if (!versionPattern.test(modelVersion)) {
            setModelVersionError('Please enter a valid number (e.g., 1 or 1.0)');
            isValid = false;
        } else {
            setModelVersionError('');
        }
        // Model Type validation
        if (!modelType) {
            setModelTypeError('Model Type is required');
            isValid = false;
        } else {
            setModelTypeError('');
        }

        // Segmentation Sub-Type validation
        if (modelType === 'Segmentation' && !segmentationSubType) {
            setSegmentationSubTypeError('Segmentation Sub-Type is required.');
            isValid = false;
        } else {
            setSegmentationSubTypeError('');
        }

        // Model email Account
        if (!validateEmail(newlyAddedEmails)) {
            setEmailError('Please enter a valid email address');
            isValid = false;
        } else {
            setEmailError('');
        }
        metrics.forEach((metric, index) => {
            const fieldErrors = {};

            // Validation for 'key'
            if (!metric.key) {
                fieldErrors.key = 'Metric Key is required.';
                isValid = false;
            }

            // Validation for value1 (Accuracy)
            const value1 = parseFloat(metric.value[0]);
            if (Number.isNaN(value1) || value1 < 0.1 || value1 > 1.0) {
                fieldErrors.value1 = 'Metric value must be between 0.1 and 1.0.';
                isValid = false;
            }

            // Validation for value2 (Threshold)
            const value2 = parseFloat(metric.value[1]);
            if (Number.isNaN(value2) || value2 < 0.1 || value2 > 1.0) {
                fieldErrors.value2 = 'Metric threshold value must be between 0.1 and 1.0.';
                isValid = false;
            }

            const updatedErrors = [...metricsError];
            updatedErrors[index] = {
                ...updatedErrors[index],
                ...fieldErrors,
            };

            setMetricsError(updatedErrors);
        });
        // Validate newly added metrics
        newMetric.forEach((metric, index) => {
            const fieldErrors = {};

            // Validation for 'key'
            if (!metric.key) {
                fieldErrors.key = 'Metric Key is required.';
                isValid = false;
            }

            // Validation for value1 (Accuracy)
            const value1 = parseFloat(metric.value[0]);
            if (Number.isNaN(value1) || value1 < 0.1 || value1 > 1.0) {
                fieldErrors.value1 = 'Metric value must be between 0.1 and 1.0.';
                isValid = false;
            }

            // Validation for value2 (Threshold)
            const value2 = parseFloat(metric.value[1]);
            if (Number.isNaN(value2) || value2 < 0.1 || value2 > 1.0) {
                fieldErrors.value2 = 'Metric threshold value must be between 0.1 and 1.0.';
                isValid = false;
            }

            const updatedErrors = [...newMetricsError];
            updatedErrors[index] = {
                ...updatedErrors[index],
                ...fieldErrors,
            };

            setNewMetricsError(updatedErrors);
        });

        return isValid;
    };
    const prepareTagsData = () => {
        const allMetrics = [...metrics, ...newMetric];
        const metricsData = allMetrics.reduce((acc, metric) => {
            if (metric.value[0] || metric.value[1]) {
                acc.push({
                    mkey: metric.key,
                    mvalue: `[${metric.value.join(', ')}]`,
                });
            }
            return acc;
        }, []);

        // Create model_metrics entry
        const modelMetricsEntry = {
            mkey: 'model_metrics',
            mvalue: `['${metricsData.map((m) => m.mkey).join("', '")}']`,
        };

        // Create model_type entry
        const modelTypeEntry = {
            mkey: 'model_type',
            mvalue: modelType,
        };

        const segmentationSubTypeEntry = {
            mkey: 'segmentation_sub_type',
            mvalue: segmentationSubType,
        };

        // Create distribution_list entry
        const distributionListEntry = {
            mkey: 'distribution_list',
            mvalue: newlyAddedEmails.join(','),
        };
        // Combine all into tags array
        const baseTags = [modelMetricsEntry, ...metricsData, modelTypeEntry, distributionListEntry];
        if (modelType === 'Segmentation' && segmentationSubType) {
            baseTags.push(segmentationSubTypeEntry);
        }
        return baseTags;
    };

    const handleSaveProject = () => {
        if (!validateForm()) {
            return;
        }
        const tags = prepareTagsData();
        // Store model details
        const storeModelDetails = {
            model_name: modelName,
            model_version: String(modelVersion),
            model_desc: modelDescription,
            model_updatedby_gpid: getLoggedUserGPID(),
            model_updatedby_user_details: {
                name: getLoggedUserFullName(),
                mail_address: LoggedUserEmailId(),
            },
            tags,
        };
        localStorage.setItem('editModelFormData', JSON.stringify(storeModelDetails));
        setModelName('');
        setModelDescription('');
        history.push('/pepvigil-edit-existing-modeltags', {
            state: activeModelData,
        });
    };
    const handleCancel = () => {
        history.push('/pepvigil-model-versions');
        localStorage.removeItem('editModelFormData');
        localStorage.removeItem('initialModelData');
        localStorage.removeItem('activeModelData');
        setModelName('');
        setModelDescription('');
        setModelNameError('');
    };
    const handleProject = () => {
        history.push('/pepvigil-projects');
        localStorage.removeItem('editModelFormData');
        localStorage.removeItem('activeModelData');
        setModelName('');
        setModelDescription('');
        setModelNameError('');
    };
    const handleMetricChange = (index, field, value) => {
        const updatedMetrics = [...metrics];
        const updatedErrors = [...metricsError];
        let error = '';

        if (field === 'value1') {
            const value1 = parseFloat(value);
            if (Number.isNaN(value1) || value1 < 0.1 || value1 > 1.0) {
                error = 'Metric value must be between 0.1 and 1.0';
            }
            updatedMetrics[index].value[0] = value;
            updatedErrors[index] = { ...updatedErrors[index], value1: error };
        }

        if (field === 'value2') {
            const value2 = parseFloat(value);
            if (Number.isNaN(value2) || value2 < 0.1 || value2 > 1.0) {
                error = 'Metric threshold value must be between 0.1 and 1.0';
            }
            updatedMetrics[index].value[1] = value;
            updatedErrors[index] = { ...updatedErrors[index], value2: error };
        }

        setMetrics(updatedMetrics);
        setMetricsError(updatedErrors);
    };

    const handleAddMetric = () => {
        const newEntry = { key: '', value: ['', ''] };
        setNewMetric((prevMetrics) => [...prevMetrics, newEntry]);
        setNewMetricsError([...newMetricsError, { key: '', value1: '', value2: '' }]);
    };
    const handleRemoveMetric = (index) => {
        const updatedNewMetric = newMetric.filter((_, i) => i !== index);
        setNewMetric(updatedNewMetric);
        const updatedErrors = newMetricsError.filter((_, i) => i !== index);
        setNewMetricsError(updatedErrors);
    };

    const handleNewMetricChange = (index, field, value) => {
        // Prepare updated metric
        const updatedNewMetrics = [...newMetric];
        const updatedErrors = [...newMetricsError];
        let error = '';
        if (field === 'value1' || field === 'value2') {
            const parsedValue = parseFloat(value);

            // Validate the parsed value
            if (Number.isNaN(parsedValue) || parsedValue < 0.1 || parsedValue > 1.0) {
                error = `Metric ${field === 'value1' ? 'value' : 'threshold'} must be between 0.1 and 1.0`;
            } else {
                error = '';
            }

            // Update the corresponding value in the 'value' array (index 0 for 'value1', index 1 for 'value2')
            const valueIndex = field === 'value1' ? 0 : 1;
            updatedNewMetrics[index].value[valueIndex] = value;

            // Set error for the specific field
            updatedErrors[index] = { ...updatedErrors[index], [field]: error };
        } else {
            // Handle changes for 'key' (optional key validation)
            const trimmedValue = value.trim();
            updatedNewMetrics[index].key = trimmedValue;

            // You can add validation for 'key', for example, checking if it's empty
            if (!trimmedValue) {
                updatedErrors[index] = { ...updatedErrors[index], key: 'Metric Key is required.' };
            } else {
                updatedErrors[index] = { ...updatedErrors[index], key: '' };
            }
        }
        // Update state with the new metrics and errors
        setNewMetric(updatedNewMetrics);
        setNewMetricsError(updatedErrors);
    };
    return (
        <ErrorBoundary FallbackComponent={ErrorFallback}>
            {' '}
            <Grid container>
                <Grid item xs={12}>
                    <Typography variant="h5">
                        <Item sx={style.subHeader}>
                            <div
                                style={{
                                    // marginLeft: '28px',
                                    marginTop: '8px',
                                    fontSize: '14px',
                                    textAlign: 'left',
                                }}
                                className="contentContainer"
                            >
                                <span>{PEPVIGIL}</span>
                                <img src={breadcrumbIcon} alt=" bredcrumb" style={style.breadcrumbIcon} />
                                <span aria-hidden="true" style={{ cursor: 'pointer', fontWeight: 'bold' }} onClick={() => handleProject()}>
                                    Projects
                                </span>
                                <img src={breadcrumbIcon} alt=" bredcrumb" style={style.breadcrumbIcon} />
                                <span aria-hidden="true" style={{ cursor: 'pointer', fontWeight: 'bold' }} onClick={() => handleCancel()}>
                                    Model Versions
                                </span>
                                <img src={breadcrumbIcon} alt=" bredcrumb" style={style.breadcrumbIcon} />
                                <span style={{ fontWeight: 'bold', color: '#005CBC' }}>Edit Model</span>
                            </div>
                        </Item>
                    </Typography>
                </Grid>
            </Grid>
            <Box className="contentContainer">
                <Box
                    sx={{
                        top: '164px',
                        // left: '27px',
                        // width: '1500px',
                        height: '70px',
                        background: '#FFFFFF 0% 0% no-repeat padding-box',
                        borderRadius: '4px',
                        opacity: 1,
                        marginTop: '20px',
                        padding: '16px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        // marginLeft: '100px',
                    }}
                >
                    <Typography sx={{ fontSize: '18px', fontWeight: '700', color: '#005CBC' }} aria-label="Edit Existing Model">
                        Edit Model
                    </Typography>
                </Box>
                <Box
                    sx={{
                        top: '164px',
                        // left: '27px',
                        // width: '1500px',
                        background: '#FFFFFF 0% 0% no-repeat padding-box',
                        borderRadius: '4px',
                        opacity: 1,
                        marginTop: '20px',
                        padding: '16px',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        // marginLeft: '100px',
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'flex-start',
                        }}
                    >
                        <StyleTextField
                            variant="filled"
                            fullWidth
                            value={modelName}
                            onChange={(e) => {
                                setModelName(e.target.value);
                                if (modelNameError) setModelNameError('');
                            }}
                            label={
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <Typography>MODEL NAME</Typography>
                                    <Typography sx={{ color: '#d40020', marginLeft: '2px' }}>*</Typography>
                                </Box>
                            }
                            error={!!modelNameError}
                            helperText={modelNameError}
                            sx={{
                                marginBottom: '20px',
                                // '& .MuiFilledInput-root': {
                                //     backgroundColor: '#F2F2F2',
                                // },
                                // '& .MuiInputLabel-root': {
                                //     color: '#3A3A3A',
                                //     fontSize: '15px',
                                // },
                                // '& .MuiInputLabel-root.Mui-focused': {
                                //     color: '#888888',
                                // },
                                width: '762px',
                                // height: '55px',
                                fontSize: '16px',
                                fontWeight: 'medium',
                            }}
                            InputProps={{
                                readOnly: true,
                            }}
                        />
                        {/* {error && <Typography sx={{ color: '#FF0000', fontSize: '14px', marginBottom: '10px' }}>{error}</Typography>} */}
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'flex-start',
                                marginBottom: '20px',
                            }}
                        >
                            <Box
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    width: '100%',
                                }}
                            >
                                <Typography sx={{ fontSize: '14px', fontWeight: 'medium', color: '#3A3A3A' }} aria-label="Model Description">
                                    Model Description
                                </Typography>
                                <Typography
                                    sx={{
                                        marginLeft: '10px',
                                        color: '#3A3A3A',
                                        fontSize: '13px',
                                        alignSelf: 'flex-start',
                                    }}
                                >
                                    Optional
                                </Typography>
                            </Box>
                            <StyleTextField
                                variant="filled"
                                fullWidth
                                multiline
                                rows={4}
                                value={modelDescription}
                                onChange={(e) => {
                                    setModelDescription(e.target.value);
                                }}
                                sx={{
                                    marginTop: '8px',
                                    // '& .MuiFilledInput-root': {
                                    //     background: '#FFFFFF 0% 0% no-repeat padding-box',
                                    // },
                                    // '& .MuiInputLabel-root': {
                                    //     color: '#3A3A3A',
                                    //     fontSize: '15px',
                                    // },
                                    // '& .MuiInputLabel-root.Mui-focused': {
                                    //     color: '#888888',
                                    // },
                                    width: '762px',
                                    // border: '1px solid #005CBC',
                                    // borderRadius: '4px',
                                }}
                            />
                        </Box>
                        <StyleTextField
                            variant="filled"
                            fullWidth
                            value={modelVersion}
                            onChange={(e) => {
                                setModelVersion(e.target.value);
                                if (modelVersionError) {
                                    setModelVersionError('');
                                }
                            }}
                            error={!!modelVersionError}
                            helperText={modelVersionError}
                            label={
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <Typography>MODEL VERSION</Typography>
                                    <Typography sx={{ color: '#d40020', marginLeft: '2px' }}>*</Typography>
                                </Box>
                            }
                            sx={{
                                marginBottom: '20px',
                                // '& .MuiFilledInput-root': {
                                //     backgroundColor: '#F2F2F2',
                                // },
                                // '& .MuiInputLabel-root': {
                                //     color: '#3A3A3A',
                                //     fontSize: '15px',
                                // },
                                // '& .MuiInputLabel-root.Mui-focused': {
                                //     color: '#888888',
                                // },
                                width: '450px',
                                // height: '55px',
                                // fontSize: '16px',
                                // fontWeight: 'medium',
                            }}
                            InputProps={{
                                readOnly: true,
                            }}
                        />
                        <FormControl variant="filled" sx={{ width: '450px', marginBottom: '20px' }} error={!!modelTypeError}>
                            <InputLabel id="model-type-label">
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <Typography sx={{ color: '#3A3A3A' }}>MODEL TYPE</Typography>
                                    <Typography sx={{ color: '#d40020', marginLeft: '2px' }}>*</Typography>
                                </Box>
                            </InputLabel>
                            <Select
                                labelId="model-type-label"
                                value={modelType}
                                onChange={(e) => {
                                    setModelType(e.target.value);
                                    if (modelTypeError) setModelTypeError('');
                                }}
                                sx={{
                                    '& .MuiFilledInput-root': {
                                        backgroundColor: '#F2F2F2',
                                    },
                                    '& .MuiInputLabel-root': {
                                        color: '#3A3A3A',
                                        fontSize: '15px',
                                    },
                                }}
                            >
                                <MenuItem value="Classification">Classification</MenuItem>
                                <MenuItem value="Detection">Detection</MenuItem>
                                <MenuItem value="Segmentation">Segmentation</MenuItem>
                            </Select>
                            {modelTypeError && <FormHelperText>{modelTypeError}</FormHelperText>}
                        </FormControl>
                        {modelType === 'Segmentation' && (
                            <FormControl variant="filled" sx={{ width: '450px', marginBottom: '20px' }} error={!!segmentationSubTypeError}>
                                <InputLabel
                                    id="segmentation-sub-type-label"
                                    sx={{
                                        '&.Mui-focused': {
                                            fontSize: '16px',
                                            fontWeight: '700',
                                            color: '#0047BA',
                                            marginTop: '-9px',
                                        },
                                    }}
                                >
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                        <Typography>SEGMENTATION SUB-TYPE</Typography>
                                        <Typography sx={{ color: '#d40020', marginLeft: '2px' }}>*</Typography>
                                    </Box>
                                </InputLabel>
                                <Select
                                    labelId="segmentation-sub-type-label"
                                    value={segmentationSubType}
                                    onChange={(e) => setSegmentationSubType(e.target.value)}
                                >
                                    <MenuItem value="Instance">INSTANCE</MenuItem>
                                    <MenuItem value="Semantic">SEMANTIC</MenuItem>
                                </Select>
                                {segmentationSubTypeError && <FormHelperText>{segmentationSubTypeError}</FormHelperText>}
                            </FormControl>
                        )}
                        <Box>
                            <Typography sx={{ fontWeight: 'medium', fontSize: '14px', color: '#3A3A3A', display: 'flex', alignItems: 'center' }}>
                                Drift notification email list
                                <Typography sx={{ color: '#d40020', marginLeft: '2px' }}>*</Typography>
                            </Typography>

                            <Grid container spacing={1} alignItems="center">
                                <Grid item>
                                    <StyleTextField
                                        id="filled-email-input"
                                        label="ADD EMAIL ACCOUNT"
                                        type="email"
                                        autoComplete="current-email"
                                        variant="filled"
                                        value={inputEmails}
                                        onChange={(e) => {
                                            setInputEmail(e.target.value);
                                            if (emailError) {
                                                setEmailError('');
                                            }
                                        }}
                                        error={!!emailError}
                                        helperText={emailError}
                                        sx={{
                                            marginBottom: '20px',
                                            // '& .MuiFilledInput-root': {
                                            //     backgroundColor: '#F2F2F2',
                                            // },
                                            // '& .MuiInputLabel-root': {
                                            //     color: '#3A3A3A',
                                            //     fontSize: '15px',
                                            // },
                                            // '& .MuiInputLabel-root.Mui-focused': {
                                            //     color: '#888888',
                                            // },
                                            width: '450px',
                                            // height: '55px',
                                            // fontSize: '16px',
                                            // fontWeight: 'medium',
                                        }}
                                    />
                                </Grid>
                                <Grid item>
                                    <Button
                                        // variant="contained"
                                        sx={{ ...styles.modalSaveButton, height: '36px', width: '121px', margin: '0 0 25px 10px' }}
                                        // sx={{
                                        //     width: '121px',
                                        //     height: '40px',
                                        //     backgroundColor: '#1378D4',
                                        //     marginTop: '-8px',
                                        // }}
                                        onClick={handleAddEmail}
                                    >
                                        Add
                                    </Button>
                                </Grid>
                            </Grid>
                        </Box>
                        <Box sx={{ marginBottom: '15px', marginTop: '0px' }}>
                            {newlyAddedEmails.map((email, index) => (
                                <Box
                                    key={index}
                                    sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        marginBottom: '10px',
                                        background: newlyAddedEmails.includes(email) ? '#E0F7FA' : '#FFFFFF',
                                        borderRadius: '4px',
                                        width: '450px',
                                        height: '60px',
                                        border: '1px solid #D6D6D6',
                                        padding: '10px',
                                        paddingTop: '15px',
                                    }}
                                >
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                            marginBottom: '8px',
                                        }}
                                    >
                                        <Typography
                                            title={email}
                                            arrow
                                            sx={{
                                                color: '#3A3A3A',
                                                fontSize: '14px',
                                                maxWidth: '300px',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap',
                                                cursor: 'pointer',
                                            }}
                                        >
                                            {email}
                                        </Typography>
                                        <Button
                                            // variant="outlined"
                                            sx={{ ...styles.modalSaveButton, height: '36px' }}
                                            // sx={{
                                            //     width: '94px',
                                            //     height: '36px',
                                            //     border: '1px solid #1378D4',
                                            //     color: '#1378D4',
                                            //     borderRadius: '4px',
                                            // }}
                                            onClick={() => handleDeleteEmail(email)}
                                        >
                                            Remove
                                        </Button>
                                    </Box>
                                </Box>
                            ))}
                        </Box>

                        <Typography sx={{ fontWeight: 'medium', fontSize: '14px', color: '#3A3A3A', display: 'flex', alignItems: 'center' }}>
                            Model Metrics
                            <Typography sx={{ color: '#d40020', marginLeft: '2px' }}>*</Typography>
                        </Typography>
                        <Box sx={{ color: '#3A3A3A', wordBreak: 'break-word', marginTop: '10px' }}>
                            {metrics.map((metric, index) => (
                                <Grid container spacing={2} alignItems="flex-start" key={index}>
                                    <Grid item xs={3}>
                                        <StyleTextField
                                            label="Metric Key"
                                            variant="outlined"
                                            value={metric.key}
                                            disabled={metric.key === 'accuracy'}
                                            onChange={(e) => handleMetricChange(index, 'key', e.target.value)}
                                        />
                                        <Box sx={{ minHeight: '20px' }}>
                                            {metricsError[index]?.general && (
                                                <Typography color="error" sx={{ marginTop: '4px' }}>
                                                    {metricsError[index]?.general}
                                                </Typography>
                                            )}
                                        </Box>
                                    </Grid>

                                    <Grid item xs={3}>
                                        <StyleTextField
                                            label="Metric value"
                                            variant="outlined"
                                            value={metric.value[0]}
                                            onChange={(e) => handleMetricChange(index, 'value1', e.target.value)}
                                        />
                                        <Box sx={{ minHeight: '20px' }}>
                                            {metricsError[index]?.value1 && (
                                                <Typography color="error" sx={{ marginTop: '4px' }}>
                                                    {metricsError[index]?.value1}
                                                </Typography>
                                            )}
                                        </Box>
                                    </Grid>

                                    <Grid item xs={3}>
                                        <StyleTextField
                                            label="Metric threshold"
                                            variant="outlined"
                                            value={metric.value[1]}
                                            onChange={(e) => handleMetricChange(index, 'value2', e.target.value)}
                                        />
                                        <Box sx={{ minHeight: '20px' }}>
                                            {metricsError[index]?.value2 && (
                                                <Typography color="error" sx={{ marginTop: '4px' }}>
                                                    {metricsError[index]?.value2}
                                                </Typography>
                                            )}
                                        </Box>
                                    </Grid>

                                    <Button
                                        // variant="contained"
                                        sx={{ ...styles.modalSaveButton, height: '36px', marginTop: '25px', marginLeft: '10px' }}
                                        // sx={{ marginTop: '30px', marginLeft: '10px', backgroundColor: '#1378D4', color: '#ffffff' }}
                                        onClick={handleAddMetric}
                                    >
                                        Add New Metric
                                    </Button>
                                </Grid>
                            ))}

                            {newMetric.map((item, index) => (
                                <Grid container spacing={2} alignItems="flex-start" sx={{ marginTop: '2px' }} key={index}>
                                    <Grid item xs={3}>
                                        <StyleTextField
                                            label="Metric Key"
                                            variant="outlined"
                                            value={item.key}
                                            onChange={(e) => handleNewMetricChange(index, 'key', e.target.value)}
                                        />
                                        <Box sx={{ minHeight: '20px' }}>
                                            {newMetricsError[index]?.key && (
                                                <Typography color="error" sx={{ marginTop: '4px' }}>
                                                    {newMetricsError[index]?.key}
                                                </Typography>
                                            )}
                                        </Box>
                                    </Grid>

                                    <Grid item xs={3}>
                                        <StyleTextField
                                            label="Metric value"
                                            variant="outlined"
                                            value={item.value[0]}
                                            onChange={(e) => handleNewMetricChange(index, 'value1', e.target.value)}
                                        />
                                        <Box sx={{ minHeight: '20px' }}>
                                            {newMetricsError[index]?.value1 && (
                                                <Typography color="error" sx={{ marginTop: '4px' }}>
                                                    {newMetricsError[index]?.value1}
                                                </Typography>
                                            )}
                                        </Box>
                                    </Grid>

                                    <Grid item xs={3}>
                                        <StyleTextField
                                            label="Metric threshold"
                                            variant="outlined"
                                            value={item.value[1]}
                                            onChange={(e) => handleNewMetricChange(index, 'value2', e.target.value)}
                                        />
                                        <Box sx={{ minHeight: '20px' }}>
                                            {newMetricsError[index]?.value2 && (
                                                <Typography color="error" sx={{ marginTop: '4px' }}>
                                                    {newMetricsError[index]?.value2}
                                                </Typography>
                                            )}
                                        </Box>
                                    </Grid>

                                    <Grid item xs={2} sx={{ marginTop: '10px' }}>
                                        <IconButton onClick={() => handleRemoveMetric(index)} aria-label="delete">
                                            <img style={{ width: '16px', height: '16px' }} src={deleteIcon} alt="deleteIcon" />
                                            {/* <DeleteIcon /> */}
                                        </IconButton>
                                    </Grid>
                                </Grid>
                            ))}
                        </Box>

                        <Box sx={{ display: 'flex', gap: '10px' }}>
                            <Button
                                // variant="outlined"
                                sx={{ ...styles.modalCancelButton, marginTop: '50px' }}
                                onClick={handleCancel}
                            >
                                Cancel
                            </Button>
                            <Button
                                // variant="contained"
                                sx={{ ...styles.modalSaveButton, marginTop: '50px', marginLeft: '500px' }}
                                // sx={{ backgroundColor: '#1378D4', marginTop: '50px', marginLeft: '500px' }}
                                onClick={handleSaveProject}
                            >
                                View Tag
                            </Button>
                        </Box>
                    </Box>
                </Box>
            </Box>
        </ErrorBoundary>
    );
};

export default EditExistingModel;
